// src/pages/Privacy.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Meteors } from '../components/ui/meteors';

import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

export default function Privacy() {
  const { darkMode, toggleDarkMode } = useTheme();
  const { 
    user, 
    userLoading, 
    isAuthLoading, 
    handleLogout, 
    openLogin, 
    openRegister 
  } = useAuth();

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      {/* Meteors Background */}
      <div className="absolute inset-0 z-[-1] pointer-events-none overflow-hidden">
        <Meteors count={35} angle={220} />
      </div>

      {/* SEO */}
      <Helmet>
        <title>Privacy Policy - SErraND Kenya</title>
        <meta
          name="description"
          content="Understand SErraND's Privacy Policy, detailing how we collect, use, and protect your personal information on our Kenyan service provider platform."
        />
        <meta
          name="keywords"
          content="SErraND, privacy policy, Kenya, service providers, data protection, personal information"
        />
        <meta name="author" content="Marvin Useki" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.serrand.org/privacy/" />
        <meta property="og:title" content="Privacy Policy - SErraND Kenya" />
        <meta
          property="og:description"
          content="SErraND's Privacy Policy explains how we safeguard your data on our platform for service providers in Kenya."
        />
        <meta property="og:url" content="https://www.serrand.org/privacy/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.serrand.org/images/privacy-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy - SErraND Kenya" />
        <meta name="twitter:description" content="SErraND's Privacy Policy for users in Kenya." />
        <meta name="twitter:image" content="https://www.serrand.org/images/privacy-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'SErraND Privacy Policy',
            url: 'https://www.serrand.org/privacy/',
            description:
              'Learn how SErraND protects your privacy and handles your personal information on our platform for service providers in Kenya.',
            publisher: {
              '@type': 'Organization',
              name: 'SErraND',
              url: 'https://www.serrand.org',
              logo: 'https://www.serrand.org/images/logo.png',
            },
          })}
        </script>
      </Helmet>

      {/* Navbar */}
      <Navbar
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
        user={user}
        userLoading={userLoading}
        isAuthLoading={isAuthLoading}
        handleLogout={handleLogout}
        openLogin={openLogin}
        openRegister={openRegister}
      />

      {/* Privacy Content */}
      <section className="relative py-16 px-4 max-w-5xl mx-auto z-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            SErraND Privacy Policy
          </h1>
          <p className="text-muted-foreground text-lg">
            Last Updated: May 31, 2025
          </p>
        </div>

        <Card className="border-border">
          <CardContent className="p-8 md:p-12 prose prose-neutral dark:prose-invert max-w-none text-foreground/90 leading-relaxed">
            
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
              <p>
                At SErraND, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our platform, SErraND.org. By accessing or using SErraND, you consent to the practices described in this policy. If you do not agree, please do not use our platform.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. Information We Collect</h2>
              <p>We collect the following types of information to provide and improve our services:</p>
              
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2.1 Personal Identification Information</h3>
              <p>
                When you register, create a profile, or contact us, we may collect your name, email address, phone number, location, and other contact details.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2.2 Profile Information</h3>
              <p>
                Service providers may share content, such as business names, service descriptions, and photos, to promote their services. This information is publicly visible to facilitate connections.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2.3 Transaction Information</h3>
              <p>
                We collect details about transactions conducted through the platform, including service details, payment amounts, and our 10% commission.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2.4 Usage Data</h3>
              <p>
                We collect data about your interactions with the platform, including IP address, browser type, device information, pages visited, and timestamps.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2.5 Communications</h3>
              <p>
                We collect the content of your communications with us, such as feedback forms, emails, or support inquiries. You consent to receive SMS, email, or other communications as part of our services.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Operate and enhance SErraND’s features, connecting clients with service providers.</li>
                <li>Process transactions and manage commissions.</li>
                <li>Personalize your experience with relevant content and recommendations.</li>
                <li>Communicate account updates, respond to inquiries, and send promotional messages.</li>
                <li>Enable promoted placements for service providers.</li>
                <li>Ensure platform security and enforce our Terms of Service.</li>
                <li>Analyze usage trends to improve our services.</li>
                <li>Comply with Kenyan legal requirements.</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. How We Share Your Information</h2>
              <p>We may share your information as follows:</p>
              
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4.1 With Other Users</h3>
              <p>
                Profile and service information you provide may be visible to other users to facilitate connections.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4.2 Service Providers</h3>
              <p>
                We share information with third-party vendors (e.g., hosting, payment processors) who assist in operating the platform.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4.3 Legal Requirements</h3>
              <p>
                We may disclose information to comply with Kenyan law or respond to legal requests, such as court orders.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4.4 Business Transfers</h3>
              <p>
                Your information may be transferred during a merger, acquisition, or sale of SErraND’s assets.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4.5 With Your Consent</h3>
              <p>
                We may share information with your explicit permission.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. Data Security</h2>
              <p>
                We implement industry-standard security measures, such as encryption and access controls, to protect your information. However, no system is completely secure, and we cannot guarantee absolute protection.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. Data Retention</h2>
              <p>
                We retain your information for as long as needed to provide services, comply with legal obligations, resolve disputes, or enforce agreements. You may request deletion of your data, subject to legal exceptions.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Your Rights</h2>
              <p>Under Kenyan law, you may have the following rights:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li><strong>Access</strong>: Request a copy of your personal information.</li>
                <li><strong>Correction</strong>: Update inaccurate or incomplete data.</li>
                <li><strong>Deletion</strong>: Request removal of your data, where applicable.</li>
                <li><strong>Objection</strong>: Object to certain data processing activities.</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, contact{' '}
                <a href="mailto:serrand.support@serrand.org" className="text-primary hover:underline">
                  serrand.support@serrand.org
                </a>.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Third-Party Links</h2>
              <p>
                SErraND may link to third-party websites. We are not responsible for their privacy practices. Please review their policies before sharing information.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. Children’s Privacy</h2>
              <p>
                SErraND is not intended for users under 18. We do not knowingly collect data from children. If we discover such data, we will delete it promptly.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">10. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy to reflect changes in our practices or legal requirements. We will notify you via website notices or email. Continued use of the platform constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mb-4">11. Governing Law and Jurisdiction</h2>
              <p>
                This Privacy Policy is governed by Kenyan law. Disputes shall be resolved exclusively in Kenyan courts.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">12. Contact Information</h2>
              <p>
                For questions about this Privacy Policy, contact us at{' '}
                <a href="mailto:serrand.support@serrand.org" className="text-primary hover:underline">
                  serrand.support@serrand.org
                </a>.
              </p>
            </section>

          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
